/**
 * ELK Routed Edge
 * Issue #414: Custom edge component that renders SVG paths through ELK-computed bend points.
 *
 * This edge type routes around obstacles using bend points computed by the ELK layout engine,
 * resulting in cleaner diagrams with edges that don't cross through nodes.
 */

import { memo, useMemo } from 'react';
import { EdgeProps, EdgeLabelRenderer, useViewport } from '@xyflow/react';

/**
 * A point in 2D space
 */
interface Point {
  x: number;
  y: number;
}

/**
 * Edge data containing routing information from ELK
 */
interface ElkRoutedEdgeData {
  /** Bend points computed by ELK for routing around obstacles */
  bendPoints?: Point[];
  /** Edge label */
  label?: string;
  /** Whether this is an async call */
  isAsync?: boolean;
  /** Call count for display */
  callCount?: number;
  /** Custom styles */
  labelStyle?: React.CSSProperties;
  labelBgStyle?: React.CSSProperties;
  [key: string]: unknown;
}

/**
 * Generate an SVG path string through a series of points.
 * Uses quadratic bezier curves for smooth corners at bend points.
 */
function generateSmoothPath(points: Point[], cornerRadius: number = 8): string {
  if (points.length < 2) return '';
  if (points.length === 2) {
    // Simple straight line
    return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`;
  }

  const path: string[] = [];

  // Start at the first point
  path.push(`M ${points[0].x} ${points[0].y}`);

  for (let i = 1; i < points.length - 1; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const next = points[i + 1];

    // Calculate vectors
    const v1 = { x: curr.x - prev.x, y: curr.y - prev.y };
    const v2 = { x: next.x - curr.x, y: next.y - curr.y };

    // Calculate lengths
    const len1 = Math.sqrt(v1.x * v1.x + v1.y * v1.y);
    const len2 = Math.sqrt(v2.x * v2.x + v2.y * v2.y);

    // Limit corner radius to half the shortest segment
    const maxRadius = Math.min(len1, len2) / 2;
    const radius = Math.min(cornerRadius, maxRadius);

    // Calculate points where curve starts and ends
    const ratio1 = radius / len1;
    const ratio2 = radius / len2;

    const curveStart = {
      x: curr.x - v1.x * ratio1,
      y: curr.y - v1.y * ratio1,
    };
    const curveEnd = {
      x: curr.x + v2.x * ratio2,
      y: curr.y + v2.y * ratio2,
    };

    // Line to curve start, then quadratic bezier through the corner
    path.push(`L ${curveStart.x} ${curveStart.y}`);
    path.push(`Q ${curr.x} ${curr.y} ${curveEnd.x} ${curveEnd.y}`);
  }

  // Line to the last point
  const last = points[points.length - 1];
  path.push(`L ${last.x} ${last.y}`);

  return path.join(' ');
}

/**
 * Generate a simple polyline path (straight segments, no curves)
 */
function generatePolylinePath(points: Point[]): string {
  if (points.length < 2) return '';

  const [first, ...rest] = points;
  const segments = rest.map(p => `L ${p.x} ${p.y}`);
  return `M ${first.x} ${first.y} ${segments.join(' ')}`;
}

/**
 * Calculate the midpoint of a path for label positioning
 */
function calculatePathMidpoint(points: Point[]): Point {
  if (points.length === 0) return { x: 0, y: 0 };
  if (points.length === 1) return points[0];
  if (points.length === 2) {
    return {
      x: (points[0].x + points[1].x) / 2,
      y: (points[0].y + points[1].y) / 2,
    };
  }

  // For multiple points, find the midpoint along the path length
  let totalLength = 0;
  const segmentLengths: number[] = [];

  for (let i = 1; i < points.length; i++) {
    const dx = points[i].x - points[i - 1].x;
    const dy = points[i].y - points[i - 1].y;
    const len = Math.sqrt(dx * dx + dy * dy);
    segmentLengths.push(len);
    totalLength += len;
  }

  const targetLength = totalLength / 2;
  let accumulatedLength = 0;

  for (let i = 0; i < segmentLengths.length; i++) {
    if (accumulatedLength + segmentLengths[i] >= targetLength) {
      // Midpoint is in this segment
      const remainingLength = targetLength - accumulatedLength;
      const ratio = remainingLength / segmentLengths[i];
      return {
        x: points[i].x + (points[i + 1].x - points[i].x) * ratio,
        y: points[i].y + (points[i + 1].y - points[i].y) * ratio,
      };
    }
    accumulatedLength += segmentLengths[i];
  }

  // Fallback to actual midpoint
  const midIndex = Math.floor(points.length / 2);
  return points[midIndex];
}

/**
 * Label configuration
 */
const LABEL_CONFIG = {
  minZoomToShow: 0.3,
  truncateZoom: 0.6,
  truncateLength: 15,
  bgPadding: { x: 6, y: 3 },
  bgBorderRadius: 4,
};

/**
 * ELK Routed Edge Component
 * Renders edges using bend points computed by ELK layout for obstacle avoidance.
 */
export const ElkRoutedEdge = memo(function ElkRoutedEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  label,
  labelStyle,
  labelBgStyle,
  style,
  markerEnd,
  markerStart,
  data,
}: EdgeProps) {
  const { zoom } = useViewport();
  const edgeData = data as ElkRoutedEdgeData | undefined;

  // Build the path points: source -> bend points -> target
  const pathPoints = useMemo(() => {
    const points: Point[] = [{ x: sourceX, y: sourceY }];

    if (edgeData?.bendPoints && edgeData.bendPoints.length > 0) {
      points.push(...edgeData.bendPoints);
    }

    points.push({ x: targetX, y: targetY });
    return points;
  }, [sourceX, sourceY, targetX, targetY, edgeData?.bendPoints]);

  // Generate the SVG path
  const edgePath = useMemo(() => {
    // Use smooth path with rounded corners for better aesthetics
    return generateSmoothPath(pathPoints, 10);
  }, [pathPoints]);

  // Calculate label position at path midpoint
  const labelPosition = useMemo(() => {
    return calculatePathMidpoint(pathPoints);
  }, [pathPoints]);

  // Zoom-responsive label visibility
  const showLabel = label && zoom >= LABEL_CONFIG.minZoomToShow;
  const displayLabel = useMemo(() => {
    if (!label) return '';
    const text = String(label);
    if (zoom < LABEL_CONFIG.truncateZoom && text.length > LABEL_CONFIG.truncateLength) {
      return text.substring(0, LABEL_CONFIG.truncateLength) + '...';
    }
    return text;
  }, [label, zoom]);

  // Determine if edge is animated (async)
  const isAnimated = edgeData?.isAsync || (style as React.CSSProperties & { animation?: string })?.animation;

  // Merge custom styles
  const customLabelStyle = edgeData?.labelStyle;
  const customLabelBgStyle = edgeData?.labelBgStyle;

  return (
    <>
      {/* Main edge path */}
      <path
        id={id}
        d={edgePath}
        fill="none"
        className="react-flow__edge-path"
        style={{
          stroke: '#64748b',
          strokeWidth: 1.5,
          ...style,
        }}
        markerEnd={markerEnd}
        markerStart={markerStart}
      />

      {/* Animated dashed overlay for async edges */}
      {isAnimated && (
        <path
          d={edgePath}
          fill="none"
          style={{
            stroke: '#3b82f6',
            strokeWidth: 2,
            strokeDasharray: '5 5',
            animation: 'dashdraw 0.5s linear infinite',
          }}
        />
      )}

      {/* Edge label */}
      {showLabel && (
        <EdgeLabelRenderer>
          <div
            className="nodrag nopan pointer-events-auto"
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelPosition.x}px, ${labelPosition.y}px)`,
              fontSize: 10,
              fontWeight: 500,
              background: 'white',
              padding: `${LABEL_CONFIG.bgPadding.y}px ${LABEL_CONFIG.bgPadding.x}px`,
              borderRadius: LABEL_CONFIG.bgBorderRadius,
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              whiteSpace: 'nowrap',
              maxWidth: 120,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              zIndex: 1000,
              transition: 'opacity 0.2s ease',
              opacity: zoom < LABEL_CONFIG.minZoomToShow + 0.1 ? 0.7 : 1,
              ...labelBgStyle,
              ...customLabelBgStyle,
            }}
          >
            <span style={{ color: '#475569', ...labelStyle, ...customLabelStyle }}>
              {displayLabel}
            </span>
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
});

/**
 * Orthogonal ELK Routed Edge
 * Same as ElkRoutedEdge but uses straight segments without curves.
 * Better for strictly orthogonal layouts.
 */
export const ElkOrthogonalEdge = memo(function ElkOrthogonalEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  label,
  labelStyle,
  labelBgStyle,
  style,
  markerEnd,
  markerStart,
  data,
}: EdgeProps) {
  const { zoom } = useViewport();
  const edgeData = data as ElkRoutedEdgeData | undefined;

  // Build path points
  const pathPoints = useMemo(() => {
    const points: Point[] = [{ x: sourceX, y: sourceY }];
    if (edgeData?.bendPoints && edgeData.bendPoints.length > 0) {
      points.push(...edgeData.bendPoints);
    }
    points.push({ x: targetX, y: targetY });
    return points;
  }, [sourceX, sourceY, targetX, targetY, edgeData?.bendPoints]);

  // Generate polyline path (straight segments)
  const edgePath = useMemo(() => {
    return generatePolylinePath(pathPoints);
  }, [pathPoints]);

  // Calculate label position
  const labelPosition = useMemo(() => {
    return calculatePathMidpoint(pathPoints);
  }, [pathPoints]);

  const showLabel = label && zoom >= LABEL_CONFIG.minZoomToShow;
  const displayLabel = useMemo(() => {
    if (!label) return '';
    const text = String(label);
    if (zoom < LABEL_CONFIG.truncateZoom && text.length > LABEL_CONFIG.truncateLength) {
      return text.substring(0, LABEL_CONFIG.truncateLength) + '...';
    }
    return text;
  }, [label, zoom]);

  const customLabelStyle = edgeData?.labelStyle;
  const customLabelBgStyle = edgeData?.labelBgStyle;

  return (
    <>
      <path
        id={id}
        d={edgePath}
        fill="none"
        className="react-flow__edge-path"
        style={{
          stroke: '#64748b',
          strokeWidth: 1.5,
          ...style,
        }}
        markerEnd={markerEnd}
        markerStart={markerStart}
      />
      {showLabel && (
        <EdgeLabelRenderer>
          <div
            className="nodrag nopan pointer-events-auto"
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelPosition.x}px, ${labelPosition.y}px)`,
              fontSize: 10,
              fontWeight: 500,
              background: 'white',
              padding: `${LABEL_CONFIG.bgPadding.y}px ${LABEL_CONFIG.bgPadding.x}px`,
              borderRadius: LABEL_CONFIG.bgBorderRadius,
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              whiteSpace: 'nowrap',
              maxWidth: 120,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              zIndex: 1000,
              ...labelBgStyle,
              ...customLabelBgStyle,
            }}
          >
            <span style={{ color: '#475569', ...labelStyle, ...customLabelStyle }}>
              {displayLabel}
            </span>
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
});

/**
 * Export edge types for registration
 */
export const elkEdgeTypes = {
  elkRouted: ElkRoutedEdge,
  elkOrthogonal: ElkOrthogonalEdge,
};
